// ============================================================
// Kasir shell — sidebar, sesi POS, order panel
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

function openingCashKey() {
  const uid = document.querySelector('meta[name="panel-user-id"]')?.content;
  return uid ? `kasir_opening_cash:${uid}` : 'kasir_opening_cash';
}

function createKasirLayout() {
  const initialSection =
    document.querySelector('meta[name="kasir-open-menu-slug"]')?.content?.trim() || null;

  return {
    sidebarOpen: false,
    openSectionSlug: initialSection,
    currentTime: '',
    sessionActive: false,
    sessionId: null,
    sessionStartTime: '',
    sessionSales: 0,
    sessionSummary: { total_transactions: 0, total_sales: 0, cash_sales: 0, non_cash_sales: 0 },
    showOpenSessionModal: false,
    showCloseSessionModal: false,
    isLoadingSession: false,
    openingCash: 0,
    openingNotes: '',
    closingCash: 0,
    closingNotes: '',
    _sessionAbort: null,

    get cashDiff() {
      const expected = (parseFloat(localStorage.getItem(openingCashKey()) || '0')) + this.sessionSummary.cash_sales;
      return this.closingCash - expected;
    },

    async init() {
      if (!this.openSectionSlug) {
        const menuSlug = document.querySelector('meta[name="kasir-open-menu-slug"]')?.content?.trim();
        if (menuSlug) this.openSectionSlug = menuSlug;
      }
      this.startClock();
      await this.checkSession();
      setInterval(() => {
        if (this.sessionActive) this.refreshSessionSummary();
      }, 30000);
      this.$nextTick(() => { if (window.hydrateLucide) window.hydrateLucide(); });
      setTimeout(() => { if (window.hydrateLucide) window.hydrateLucide(); }, 150);
    },

    closeSidebar() {
      this.sidebarOpen = false;
    },

    toggleSection(slug) {
      this.openSectionSlug = this.openSectionSlug === slug ? null : slug;
    },

    /** @deprecated alias — nested pakai x-data lokal */
    toggleMenu(slug) {
      this.toggleSection(slug);
    },

    startClock() {
      const tick = () => {
        this.currentTime = new Date().toLocaleTimeString('id-ID', {
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
      };
      tick();
      setInterval(tick, 1000);
    },

    async checkSession() {
      try {
        const { data } = await axios.get('/api/v1/kasir/pos/session');
        if (data.data?.has_active_session) {
          this.sessionActive = true;
          this.sessionId = data.data.session_id;
          this.sessionStartTime = data.data.opened_at?.split(' ')[1]?.substring(0, 5) || '';
          this.sessionSales = data.data.summary?.total_sales || 0;
          this.sessionSummary = data.data.summary || {};
        }
      } catch (e) {}
    },

    async refreshSessionSummary() {
      if (!this.sessionActive) return;
      try {
        const { data } = await axios.get('/api/v1/kasir/pos/session');
        this.sessionSales = data.data?.summary?.total_sales || 0;
        this.sessionSummary = data.data?.summary || {};
      } catch (e) {}
    },

    openSession() {
      this.cancelSessionAction();
      this.showOpenSessionModal = true;
    },

    async closeSession() {
      this.cancelSessionAction();
      await this.refreshSessionSummary();
      this.showCloseSessionModal = true;
    },

    cancelSessionAction() {
      if (this._sessionAbort) {
        this._sessionAbort.abort();
        this._sessionAbort = null;
      }
      this.isLoadingSession = false;
    },

    cancelOpenSession() {
      this.cancelSessionAction();
      this.showOpenSessionModal = false;
    },

    cancelCloseSession() {
      this.cancelSessionAction();
      this.showCloseSessionModal = false;
    },

    async submitOpenSession() {
      if (this.isLoadingSession) return;
      if (this.openingCash < 0) {
        window.toast('Masukkan nominal uang awal.', 'warning');
        return;
      }
      this.isLoadingSession = true;
      this._sessionAbort = new AbortController();
      try {
        const { data } = await axios.post('/api/v1/kasir/pos/open-session', {
          opening_cash: this.openingCash,
          notes: this.openingNotes,
        }, { signal: this._sessionAbort.signal, timeout: 30000 });
        this.sessionActive = true;
        this.sessionId = data.data.session_id;
        this.sessionStartTime = data.data.opened_at?.split(' ')[1]?.substring(0, 5) || '';
        localStorage.setItem(openingCashKey(), String(this.openingCash));
        localStorage.removeItem('kasir_opening_cash');
        this.showOpenSessionModal = false;
        this.openingCash = 0;
        this.openingNotes = '';
        window.toast('Sesi kasir dibuka!', 'success');
      } catch (e) {
        if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return;
        window.toast(e.response?.data?.message || 'Gagal membuka sesi.', 'error');
      } finally {
        this.isLoadingSession = false;
        this._sessionAbort = null;
      }
    },

    async submitCloseSession() {
      if (this.isLoadingSession) return;
      this.isLoadingSession = true;
      this._sessionAbort = new AbortController();
      try {
        const { data } = await axios.post('/api/v1/kasir/pos/close-session', {
          closing_cash: this.closingCash,
          notes: this.closingNotes,
        }, { signal: this._sessionAbort.signal, timeout: 30000 });
        this.sessionActive = false;
        this.sessionId = null;
        this.sessionSales = 0;
        this.showCloseSessionModal = false;
        window.toast('Sesi ditutup. Total: Rp ' + this.formatRP(data.data?.summary?.total_sales || 0), 'success');
        setTimeout(() => location.reload(), 1000);
      } catch (e) {
        if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return;
        window.toast(e.response?.data?.message || 'Gagal menutup sesi.', 'error');
      } finally {
        this.isLoadingSession = false;
        this._sessionAbort = null;
      }
    },

    formatRP(n) {
      return Number(n || 0).toLocaleString('id-ID');
    },
  };
}

Alpine.data('kasirLayout', createKasirLayout);
